/**
 * Iter 64 — Deletion Requests queue.
 *
 * Two tabs:
 *   1. Pending my approval (RM view)
 *   2. Submitted by me     (requester view)
 *
 * Super Admins see a third "All requests" tab.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Check, X, Clock, AlertOctagon, Loader2, RefreshCw, FileText } from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '../../components/PageHeader';
import { useConfirm } from '../../components/ConfirmDialog';
import { apiGet, apiPut } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

function fmt(ts) {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return ts; }
}

const STATUS_STYLE = {
  pending: 'bg-amber-100 text-amber-800 border border-amber-300',
  approved: 'bg-emerald-100 text-emerald-800 border border-emerald-300',
  rejected: 'bg-rose-100 text-rose-800 border border-rose-300',
};

export default function DeletionRequests() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const [confirm, ConfirmHost] = useConfirm();
  const [tab, setTab] = useState('mine');  // 'mine' | 'submitted_by_me' | 'all'
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const isSuper = user?.role === 'super_admin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = tab === 'all' ? '' : `?queue=${tab}`;
      const r = await apiGet(`/delete-requests${q}`);
      setRows(r.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const decide = async (row, kind) => {
    const action = kind === 'approve' ? 'Approve & archive' : 'Reject';
    const ok = await confirm({
      title: `${action} this deletion?`,
      body: kind === 'approve'
        ? `You are about to APPROVE the deletion of:\n\n${row.entity_label}: ${row.entity_name}\nRequested by: ${row.requested_by_name}\nReason: ${row.reason}\n\nThe underlying record will be soft-archived immediately. This decision is logged in the audit log as CRITICAL.`
        : `You are about to REJECT the deletion of:\n\n${row.entity_label}: ${row.entity_name}\nRequested by: ${row.requested_by_name}\n\nThe entity stays intact. ${row.requested_by_name} will be notified.`,
      confirmLabel: action,
      danger: kind === 'approve',
    });
    if (!ok) return;
    const note = prompt('Add a note for the requester (optional):') || '';
    try {
      await apiPut(`/delete-requests/${row.id}/${kind}`, { decision_note: note });
      toast.success(`${kind === 'approve' ? 'Approved' : 'Rejected'}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const TABS = [
    { key: 'mine', label: 'Pending My Approval', icon: AlertOctagon },
    { key: 'submitted_by_me', label: 'Submitted by Me', icon: FileText },
    ...(isSuper ? [{ key: 'all', label: 'All Requests', icon: Shield }] : []),
  ];

  return (
    <div className="p-6 md:p-8" data-testid="deletion-requests-page">
      {ConfirmHost}
      <button onClick={() => navigate('/app/settings')} className="text-xs font-display font-bold uppercase tracking-wider text-slate-500 hover:text-[#1F7A6E] flex items-center gap-1 mb-3"><ArrowLeft size={12} /> Back to Settings</button>
      <PageHeader overline="Settings · Governance" title="Deletion Approval Queue" subtitle="Major-entity deletions require Reporting Manager approval. All decisions are audit-logged as CRITICAL." icon={Shield} />

      <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
        <div className="flex border border-slate-200 rounded-sm overflow-hidden">
          {TABS.map((t) => {
            const TIcon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2 text-xs font-display font-bold uppercase tracking-wider flex items-center gap-1.5 ${tab === t.key ? 'bg-[#1F7A6E] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                data-testid={`tab-${t.key}`}
              >
                <TIcon size={11} /> {t.label}
              </button>
            );
          })}
        </div>
        <button onClick={load} className="text-xs px-3 py-1.5 border border-[#1F7A6E] text-[#1F7A6E] rounded-sm hover:bg-teal-50 flex items-center gap-1 font-display font-bold uppercase tracking-wider" data-testid="refresh-btn">
          <RefreshCw size={11} /> Refresh
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden mt-4">
        {loading ? (
          <div className="py-14 text-center text-sm text-slate-500"><Loader2 size={14} className="animate-spin inline mr-2" /> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="py-14 text-center text-sm text-slate-500">No requests in this queue.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-display">
              <tr>
                <th className="text-left py-2.5 px-3 w-32">Submitted</th>
                <th className="text-left py-2.5 px-3 w-24">Status</th>
                <th className="text-left py-2.5 px-3">Entity</th>
                <th className="text-left py-2.5 px-3">Requested By</th>
                <th className="text-left py-2.5 px-3">Approver</th>
                <th className="text-left py-2.5 px-3">Reason</th>
                <th className="text-left py-2.5 px-3 w-44">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`req-row-${r.id}`}>
                  <td className="py-2 px-3 text-xs text-slate-600 whitespace-nowrap">{fmt(r.created_at)}</td>
                  <td className="py-2 px-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-display font-bold uppercase tracking-wider rounded-sm ${STATUS_STYLE[r.status]}`}>
                      {r.status === 'pending' && <Clock size={10} />}
                      {r.status === 'approved' && <Check size={10} />}
                      {r.status === 'rejected' && <X size={10} />}
                      {r.status}
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    <div className="text-sm font-semibold">{r.entity_label}</div>
                    <div className="text-[11px] font-mono text-slate-500">{r.entity_name}</div>
                    {r.contract_value ? <div className="text-[10px] text-rose-700">₹{r.contract_value.toLocaleString('en-IN')}</div> : null}
                  </td>
                  <td className="py-2 px-3">
                    <div className="text-sm">{r.requested_by_name}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{r.requested_by_email}</div>
                  </td>
                  <td className="py-2 px-3 text-xs">
                    <div>{r.approver_name}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{r.approver_email}</div>
                  </td>
                  <td className="py-2 px-3 text-xs max-w-xs truncate" title={r.reason}>{r.reason}</td>
                  <td className="py-2 px-3">
                    {r.status === 'pending' && (r.approver_email === user?.email || isSuper) ? (
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => decide(r, 'approve')} className="px-2.5 py-1 text-[10px] font-display font-bold uppercase bg-emerald-600 text-white rounded-sm hover:bg-emerald-700" data-testid={`approve-${r.id}`}>
                          Approve
                        </button>
                        <button onClick={() => decide(r, 'reject')} className="px-2.5 py-1 text-[10px] font-display font-bold uppercase bg-rose-600 text-white rounded-sm hover:bg-rose-700" data-testid={`reject-${r.id}`}>
                          Reject
                        </button>
                      </div>
                    ) : r.decided_by ? (
                      <div className="text-[10px] text-slate-500">
                        by {r.decided_by_name || r.decided_by}<br />
                        {fmt(r.decided_at)}
                        {r.decision_note && <div className="italic mt-0.5 text-slate-400">"{r.decision_note}"</div>}
                      </div>
                    ) : <span className="text-[10px] text-slate-400">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
